import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import {
    TIMERECORDING_ENTRY_VALIDITY,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from '../../models/complex/time-recording-entry.model';
import {
    TimeRecordingGeneralEntryModel,
    isTimeRecordingGeneralEntryModel,
} from '../../models/complex/time-recording-general-entry.model';
import { TimeRecordingValidationResultModel } from '../../models/complex/time-recording-validation-result.model';
import { TimesheetEntryInterface } from '../../models/complex/timesheet-entry.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { TIMERECORDING_VALIDITY } from '../../types/timerecording-validity';
import { TimeUtilities } from '../../utilities/time-utilities';
import { ConfigService } from '../config/config.service';
import { TimeRecordingService } from '../time-recording/time-recording.service';
import { TimesheetAndExpensesService } from './timesheet-and-expenses.service';

@Injectable({
    providedIn: 'root',
})
export class TimeRecordingValidationService {
    public constructor(
        private readonly translate: TranslateService,
        private readonly timeRecordingService: TimeRecordingService,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        private readonly valueHelperService: ValueHelperService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly configService: ConfigService,
    ) {}

    public performValidation(
        notification: NotificationIdentifierModel,
        timeRecordingEntries: TimeRecordingEntryModel[],
        otherTimeRecordingEntries: TimesheetEntryInterface[],
        isRemoteSupport = false,
    ): TimeRecordingValidationResultModel[] {
        // if following check fails, we don't need to proceed the validation but can rather fail and return immediately
        const hardFailResult = this.checkForCorrectAmountOfEntries(timeRecordingEntries);
        if (!hardFailResult[0].isValid) {
            return hardFailResult;
        }

        let validationResultArray: TimeRecordingValidationResultModel[] = [];

        if (this.clientManager.isNgmAP0()) {
            validationResultArray = validationResultArray.concat(this.checkStartTime(timeRecordingEntries));
        }
        validationResultArray = validationResultArray.concat(
            this.checkAllEntriesForValidTypeOfWorkCodes(timeRecordingEntries),
        );
        validationResultArray = validationResultArray.concat(this.checkOrder(timeRecordingEntries));
        validationResultArray = validationResultArray.concat(this.checkStartTimeBeforeEndTime(timeRecordingEntries));
        validationResultArray = validationResultArray.concat(
            this.checkTRBKEndDateModelAndTimeModelIsValid(timeRecordingEntries),
        );
        const canMultipleNotificationsBeInProgress = this.configService.getAllowNotificationPause();
        validationResultArray = validationResultArray.concat(this.checkForNonExistingOverlaps(timeRecordingEntries));
        if (canMultipleNotificationsBeInProgress) {
            validationResultArray = validationResultArray.concat(
                this.checkForNonExistingsOverlapsWithInprogressNotifications(
                    notification,
                    timeRecordingEntries,
                    otherTimeRecordingEntries,
                ),
            );
        }
        if (!isRemoteSupport) {
            if (!canMultipleNotificationsBeInProgress) {
                validationResultArray = validationResultArray.concat(
                    this.checkForNonExistingGaps(timeRecordingEntries),
                );
            }
            validationResultArray = validationResultArray.concat(
                this.checkForCorrectAmountAndTypeOfEntriesInPhoneFixCase(timeRecordingEntries),
            );
        }

        const date = timeRecordingEntries[0].startDateTime.clone();
        const timesheet = this.clientManager.isNgmP58()
            ? this.timesheetAndExpensesService.getCurrentTimesheet()
            : this.timesheetAndExpensesService.getTimesheetForDate(date);
        if (this.clientManager.isNgmAP0()) {
            validationResultArray = validationResultArray.concat(
                this.checkForSubmittedTimesheet(timeRecordingEntries, timesheet),
            );
        }
        validationResultArray = validationResultArray.concat(
            this.checkForNonExistingsOverlapsWithTimesheet(notification, timeRecordingEntries, timesheet),
        );
        const startOfWork = this.timesheetAndExpensesService.getStartOfWork();
        const submittedTimesheet = this.timesheetAndExpensesService.getSubmittedTimesheet();
        const endOfWork = submittedTimesheet.getEndOfWork();
        validationResultArray = validationResultArray.concat(
            this.checkForStartDateTimesBeingBeforeStartOfWorkorEndOfWork(timeRecordingEntries, startOfWork, endOfWork),
        );
        return validationResultArray;
    }

    // Time Recording Internal
    // Rules that can be checked within a time recording itself and dont have any dependency to other data.

    /**
     * If this rule returns a falsy result entry, the validation can stop right ahead and return falsy.
     * This rule checks if there is at least a single entry in the time recording. We should hard fail in case of a falsy
     * result of this rule to not SPAM the user with validation message that are obvious if there are no entries at all.
     * So making this function part of any subsequent rule is not a good idea.
     *
     * @param timeRecordingEntries time recording entries
     * @returns array of validation result entries
     */
    public checkForCorrectAmountOfEntries(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        const validationEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_VALIDITY.INVALID_NUMBER_OF_ROWS,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_INVALIDNUMBEROFROWS'),
            true,
            undefined,
        );
        validationResultArray.push(validationEntry);

        // no entries at all are never correct. We need at least a single entry for the phone fix case(NGMP58).
        if (!timeRecordingEntries || timeRecordingEntries.length < 1) {
            validationEntry.isValid = false;
        }

        return validationResultArray;
    }

    /**
     * Each time recording entry needs to have a "type of work" code selected.
     *
     * @param timeRecordingEntries time recording entries
     * @returns array of validation result entries
     */
    public checkAllEntriesForValidTypeOfWorkCodes(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            if (currentEntry instanceof TimeRecordingGeneralEntryModel) {
                const validationResultEntry = new TimeRecordingValidationResultModel(
                    TIMERECORDING_ENTRY_VALIDITY.NO_TYPE_OF_WORK_SELECTED,
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NOTYPEOFWORKSELECTED'),
                    true,
                    currentEntry.id,
                );
                validationResultArray.push(validationResultEntry);

                if (!currentEntry.typeOfWorkModel.selectedEntry) {
                    validationResultEntry.isValid = false;
                }
            }
        });

        return validationResultArray;
    }

    public checkOrder(
        timeRecordingEntries: TimeRecordingEntryModel[],
        isRemoteWork = false,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        // Phone Fix case for NGMP58 and SA
        if (this.clientManager.isNgmP58OrSA() && timeRecordingEntries.length === 1) {
            const currentEntry = timeRecordingEntries[0];
            if (
                currentEntry &&
                isTimeRecordingGeneralEntryModel(currentEntry) &&
                (isRemoteWork || currentEntry.isPhoneFix())
            ) {
                const validationResultEntry = new TimeRecordingValidationResultModel(
                    TIMERECORDING_VALIDITY.INVALID_ORDER,
                    <string>this.translate.instant(
                        'CONFIRMATION_TIMERECORDING_VALIDATION_ENDTIME_AFTER_STARTTIME_OF_NEXT',
                        {
                            endTime: currentEntry.endDateTime.format(this.clientManager.getDateTimeFormat(true)),
                            startTime: currentEntry.startDateTime.format(this.clientManager.getDateTimeFormat(true)),
                        },
                    ),
                    true,
                    currentEntry.id,
                );
                validationResultArray.push(validationResultEntry);
            }
        } else {
            // NGMP58 - If notification is Remote work
            // NGMP58 NGMAA non Phone Fix case or
            // NGMAA Phone Fix case
            for (let currentIndex = 0; currentIndex < timeRecordingEntries.length; currentIndex++) {
                const currentEntry = timeRecordingEntries[currentIndex];
                let nextEntry;
                if (currentIndex < timeRecordingEntries.length - 1) {
                    nextEntry = timeRecordingEntries[currentIndex + 1];
                }

                if (nextEntry) {
                    const validationResultEntry = new TimeRecordingValidationResultModel(
                        TIMERECORDING_VALIDITY.INVALID_ORDER,
                        <string>this.translate.instant(
                            'CONFIRMATION_TIMERECORDING_VALIDATION_ENDTIME_AFTER_STARTTIME_OF_NEXT',
                            {
                                endTime: currentEntry.endDateTime.format(this.clientManager.getDateTimeFormat(true)),
                                startTime: nextEntry.startDateTime.format(this.clientManager.getDateTimeFormat(true)),
                            },
                        ),
                        true,
                        currentEntry.id,
                    );
                    validationResultArray.push(validationResultEntry);

                    if (currentEntry.endDateTime.isAfter(nextEntry.startDateTime)) {
                        validationResultEntry.isValid = false;
                    }
                }
            }
        }

        return validationResultArray;
    }

    /**
     * Check if TRBK end time
     *
     * @param timeRecordingEntries time recording entries
     * @returns array of validation result entries
     */
    public checkTRBKEndDateModelAndTimeModelIsValid(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        const isEndTimeInvalid = timeRecordingEntries.some((entry) => !entry.endTimeModel.value.isValid());
        const errorMessage = isEndTimeInvalid ? 'End time' : '';
        const lastEntryId = timeRecordingEntries[timeRecordingEntries.length - 1].id;

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_VALIDITY.ENDTIME_BEFORE_STARTTIME,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_TRBK_ENDTIME_INVALID', {
                model: errorMessage,
            }),
            true,
            lastEntryId,
        );

        validationResultEntry.isValid = errorMessage.length === 0;
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }
    /**
     * An entry should not have an end time which is before its start time.
     *
     * @param timeRecordingEntries time recording entries
     * @returns array of validation result entries
     */
    public checkStartTimeBeforeEndTime(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_VALIDITY.ENDTIME_BEFORE_STARTTIME,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_ENDTIME_BEFORE_STARTTIME', {
                    endTime: currentEntry.endDateTime.format(this.clientManager.getDateTimeFormat(true)),
                    startTime: currentEntry.startDateTime.format(this.clientManager.getDateTimeFormat(true)),
                }),
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (currentEntry.endTimeModel.value.isBefore(currentEntry.startDateTime)) {
                validationResultEntry.isValid = false;
            }
        });

        return validationResultArray;
    }

    /**
     * Gaps between entries are not permitted. This method is called recursively in case an entry has activities.
     *
     * @param timeRecordingEntries time recording entries
     * @returns array of validation result entries
     */
    public checkForNonExistingGaps(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        let precedingEntry: TimeRecordingEntryModel | undefined;
        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_VALIDITY.GAP_DETECTED,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_GAPDETECTED'),
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (precedingEntry) {
                if (currentEntry.startDateTime.isAfter(precedingEntry.endDateTime)) {
                    validationResultEntry.isValid = false;
                }
            }
            precedingEntry = currentEntry;
        });

        return validationResultArray;
    }

    /**
     * Entries should not overlap each other. This method is called recursively in case an entry has activities.
     *
     * @param timeRecordingEntries time recording entries
     * @returns array of validation result entries
     */
    public checkForNonExistingOverlaps(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        let precedingEntry: TimeRecordingEntryModel | undefined;
        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.OVERLAP_TIMERECORDING,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_OVERLAPDETECTED'),
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (precedingEntry) {
                if (
                    TimeUtilities.hasOverlap(
                        currentEntry.startDateTime,
                        currentEntry.endDateTime,
                        precedingEntry.startDateTime,
                        precedingEntry.endDateTime,
                    )
                ) {
                    validationResultEntry.isValid = false;
                }
            }
            precedingEntry = currentEntry;
        });

        return validationResultArray;
    }

    /**
     * For NGMP58 and NGMSA : In the "Phone Fix" case its not allowed to have additional rows beside the phone-fix-row.
     * For NGMAA : In the "Phone Fix" case its not allowed to have additional rows except the TRTO and TRBK rows, beside the phone-fix-row.
     *
     * @param timeRecordingEntries time recording entries
     * @returns array of validation result entries
     */
    public checkForCorrectAmountAndTypeOfEntriesInPhoneFixCase(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_VALIDITY.PHONEFIX_TOO_MANY_ROWS,
            this.clientManager.isNgmSA()
                ? <string>(
                      this.translate.instant(
                          'CONFIRMATION_TIMERECORDING_VALIDATION_REMOTE_SUPPORT_TOO_MANY_ROWS_WARNING',
                      )
                  )
                : <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_PHONEFIXTOOMANYROWS'),
            true,
            undefined,
        );
        validationResultArray.push(validationResultEntry);

        let phoneFixFlag = false;
        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            if (currentEntry instanceof TimeRecordingGeneralEntryModel) {
                // only overwrite the phone fix flag unless it hasnt been overwritten before
                if (!phoneFixFlag) {
                    phoneFixFlag = currentEntry.isPhoneFix();
                }
            }
        });

        if (phoneFixFlag) {
            if (this.clientManager.isNgmP58OrSA() && timeRecordingEntries.length > 1) {
                validationResultEntry.isValid = false;
            }
            const maxTimeRecordingEntries = 3;
            const maxTravelToStandbyEntries = 2;

            if (this.configService.getEnableStandbyTravel()) {
                validationResultEntry.isValid =
                    this.timeRecordingService.getTravelToEntries(timeRecordingEntries).length >= 1 &&
                    this.timeRecordingService.getTravelToEntries(timeRecordingEntries).length <=
                        maxTravelToStandbyEntries &&
                    this.timeRecordingService.getNonTravEntries(timeRecordingEntries).length === 1 &&
                    this.timeRecordingService.countEntriesByType(
                        timeRecordingEntries,
                        TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
                    ) === 1;
            } else if (
                this.clientManager.isNgmAA() &&
                this.timeRecordingService.countEntriesByType(
                    timeRecordingEntries,
                    TIMERECORDING_TYPE_OF_WORK_CODES.TRTO,
                ) === 1 &&
                this.timeRecordingService.countEntriesByType(
                    timeRecordingEntries,
                    TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
                ) === 1 &&
                timeRecordingEntries.length > maxTimeRecordingEntries
            ) {
                // For NGMAA check that only TRTO, TRBK, and PHONE_FIX entries exists
                validationResultEntry.isValid = false;
            }
        }

        return validationResultArray;
    }

    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    /// Time Recording External Rules
    /// Rules that need external data like End-Of-Work (EOW), Start-Of-Work (SOW) or other notifications.
    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    // No entry should be overlapping with any entry of another notification
    public checkForNonExistingsOverlapsWithTimesheet(
        notification: NotificationIdentifierModel,
        timeRecordingEntries: TimeRecordingEntryModel[],
        timesheet: TimesheetModel,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        let timesheetEntries = timesheet.getTimesheetEntries();

        timesheetEntries = timesheetEntries.filter((entry) => {
            // filter out normal entries for this notification; overlap is only calculated for other notifications
            if (TimesheetAndExpensesService.isNormalEntry(entry)) {
                return !NotificationIdentifierModel.compare(entry.notificationId, notification);
            } else {
                // ... or manual entries
                return true;
            }
        });

        const isValid = true;
        // Check if any of the timesheet entries overlap with the current time recording
        timeRecordingEntries.forEach((timerecordingEntry) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.OVERLAP_TIMESHEET,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_TIMESHEETOVERLAPDETECTED'),
                isValid,
                timerecordingEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            // iterate over all timesheet entries to check for overlap. A for-loop is used here instead of a forEach
            // so that we can stop unnecessary loops execution by using 'break'
            for (const currentTimesheetEntry of timesheetEntries) {
                // check if an overlap is possible at all
                if (
                    TimeUtilities.hasOverlap(
                        timerecordingEntry.startDateTime,
                        timerecordingEntry.endDateTime,
                        currentTimesheetEntry.startTime.value,
                        currentTimesheetEntry.endTime.value,
                    )
                ) {
                    validationResultEntry.isValid = false;

                    // we can stop after finding the first overlap for this timerecording entry
                    break;
                }
            }
        });

        return validationResultArray;
    }

    public checkForNonExistingsOverlapsWithInprogressNotifications(
        notification: NotificationIdentifierModel,
        timeRecordingEntries: TimeRecordingEntryModel[],
        otherTimeRecordingEntries: TimesheetEntryInterface[],
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const isValid = true;
        // Check if any of the timesheet entries overlap with the current time recording
        timeRecordingEntries.forEach((timerecordingEntry) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.OVERLAP_OTHER_TIMERECORDING,
                <string>(
                    this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_OTHER_TIMERECORDING_OVERLAPDETECTED')
                ),
                isValid,
                timerecordingEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            // iterate over all otherTimeRecordingEntries to check for overlap. A for-loop is used here instead of a forEach
            // so that we can stop unnecessary loops execution by using 'break'
            for (const otherTRentry of otherTimeRecordingEntries) {
                // check if an overlap is possible at all
                if (
                    TimeUtilities.hasOverlap(
                        timerecordingEntry.startDateTime,
                        timerecordingEntry.endDateTime,
                        otherTRentry.startTime.value,
                        otherTRentry.endTime.value,
                    )
                ) {
                    validationResultEntry.isValid = false;
                }
            }
        });

        return validationResultArray;
    }

    /**
     * No entry should have a startDateTime value before Start-Of-Work (SOW). This methods searches for the entry with
     * the earliest startDateTime and compares it to SOW. The entry should not be before SOW.
     * It is possible to manually save SOW with a time which is before previous EOW using timesheet screen,
     * that is why it is required to also check for EOW time so as not to allow to submit an entry having a startDateTime before EOW time
     *
     * @param timeRecordingEntries time recording entries
     * @param startOfWork the start of work
     * @param endOfWork the end of work
     * @returns array of validation result entries
     * @throws Error if no entry with minimum startDateTime is found
     */
    public checkForStartDateTimesBeingBeforeStartOfWorkorEndOfWork(
        timeRecordingEntries: TimeRecordingEntryModel[],
        startOfWork: moment.Moment,
        endOfWork: moment.Moment,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const entryWithMinimumStartDateTime = _.minBy(
            timeRecordingEntries,
            (currentTimeRecEntry: TimeRecordingEntryModel) => {
                return currentTimeRecEntry.startDateTime;
            },
        );

        if (!entryWithMinimumStartDateTime) {
            throw new Error(`Unable to find entry with minimum start date!`);
        }

        let isValid = true;
        let validationErrorMessage = '';
        if (endOfWork.isValid() && entryWithMinimumStartDateTime.startDateTime.isBefore(endOfWork)) {
            isValid = false;
            validationErrorMessage = <string>(
                this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_ENDOFWORKVIOLATED')
            );
        } else if (entryWithMinimumStartDateTime && entryWithMinimumStartDateTime.startDateTime.isBefore(startOfWork)) {
            // For ngm AA in time recording start of work can allowed to have max one previous day
            // TODO: add the validation for ngm  AA
            if (this.clientManager.isNgmAP0()) {
                isValid = true;
            } else {
                isValid = false;
                validationErrorMessage = <string>(
                    this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_STARTOFWORKVIOLATED')
                );
            }
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.START_OF_WORK_VIOLATED,
            validationErrorMessage,
            isValid,
            entryWithMinimumStartDateTime.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    /**
     * Called directly and not part of the common validation process as its purpose is to show just a warning.
     *
     * @param timeRecordingEntry time recording entry
     * @returns true if the entry has a duration of 24 hours or more
     */
    public checkDurationEqualorExceeding24Hours(timeRecordingEntry: TimeRecordingEntryModel): boolean {
        const hoursPerDay = 24;
        return this.checkDurationEqualOrExceedingHours(timeRecordingEntry, hoursPerDay);
    }

    public checkDurationEqualOrExceedingHours(timeRecordingEntry: TimeRecordingEntryModel, hours: number): boolean {
        return timeRecordingEntry.endDateTime.diff(timeRecordingEntry.startDateTime, 'hours') >= hours;
    }

    public isEntryStartingWithAGapOf10HoursOrMore(
        entries: TimeRecordingEntryModel[],
        timeRecordingEntry: TimeRecordingEntryModel,
    ): boolean {
        const gapHours = 10;
        const precedingEntry = this.timeRecordingService.getPrecedingEntry(entries, timeRecordingEntry);
        return typeof precedingEntry !== 'undefined'
            ? timeRecordingEntry.startDateTime.diff(precedingEntry.endDateTime, 'hours') >= gapHours
            : false;
    }

    public isEntriesHavingAnUnoccupiedGap(
        otherEntries: TimesheetEntryInterface[],
        entries: TimeRecordingEntryModel[],
        timeRecordingEntry: TimeRecordingEntryModel,
    ): boolean {
        const subsequentEntry = this.timeRecordingService.getSubsequentEntry(entries, timeRecordingEntry);
        const isGap =
            typeof subsequentEntry !== 'undefined' &&
            subsequentEntry.startDateTime.diff(timeRecordingEntry.endDateTime, 'minutes') > 0;
        if (isGap && otherEntries.length > 0) {
            // check if gap is occupied by single entry
            const isGapOccupied = otherEntries.some(
                (entry) =>
                    entry.startTime.value.diff(timeRecordingEntry.endDateTime, 'minutes') === 0 &&
                    entry.endTime.value.diff(subsequentEntry?.startDateTime, 'minutes') === 0,
            );
            // check if gap is occupied by multiple entries
            if (!isGapOccupied && otherEntries.length > 1) {
                const entryStartingAtSameTime = otherEntries.find(
                    (entry) =>
                        entry.startTime.value.diff(timeRecordingEntry.endDateTime, 'minutes') === 0 &&
                        entry.endTime.value.diff(entry.startTime.value, 'minutes') > 0,
                );
                const entryEndingAtSameTime = otherEntries.find(
                    (entry) =>
                        typeof subsequentEntry !== 'undefined' &&
                        entry.endTime.value.diff(subsequentEntry.startDateTime, 'minutes') === 0 &&
                        entry.endTime.value.diff(entry.startTime.value, 'minutes') > 0,
                );
                if (typeof entryStartingAtSameTime !== 'undefined' && typeof entryEndingAtSameTime !== 'undefined') {
                    const startEntryIndex = otherEntries.findIndex(
                        (currentEntry) => currentEntry === entryStartingAtSameTime,
                    );
                    const endEntryIndex = otherEntries.findIndex(
                        (currentEntry) => currentEntry === entryEndingAtSameTime,
                    );
                    // get all entries between the entryStartingAtSameTime and entryEndingAtSameTime
                    const subsequentEntries = otherEntries.slice(startEntryIndex, endEntryIndex + 1);

                    let previousEntry: TimesheetEntryInterface | undefined;
                    let isGapBetweenEntries = false;
                    for (const entry of subsequentEntries) {
                        if (previousEntry && entry.startTime.value.diff(previousEntry.endTime.value) > 0) {
                            isGapBetweenEntries = true;
                            break;
                        }
                        // update previous entry
                        previousEntry = entry;
                    }
                    return isGapBetweenEntries;
                } else {
                    return true;
                }
            }
            return !isGapOccupied;
        }
        return isGap;
    }

    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // NEW ENTRY VALIDATION ////////////////////////////////////////////////////////////////////////////////////////////
    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public performNewEntryValidation(
        timeRecordingEntries: TimeRecordingEntryModel[],
        newEntry: TimeRecordingGeneralEntryModel,
        isRemoteWork = false,
    ): TimeRecordingValidationResultModel[] {
        let validationResultArray: TimeRecordingValidationResultModel[] = [];

        validationResultArray = validationResultArray.concat(this.checkTypeOfWork(newEntry));
        if (this.clientManager.isNgmP58()) {
            validationResultArray = validationResultArray.concat(this.checkActivity(newEntry));
        }
        if (!isRemoteWork) {
            validationResultArray = validationResultArray.concat(
                this.checkIfNewEntryStartsBeforeTRTO(timeRecordingEntries, newEntry),
            );
            validationResultArray = validationResultArray.concat(
                this.checkIfNewEntryStartsLaterThanTRBK(timeRecordingEntries, newEntry),
            );
        }
        validationResultArray = validationResultArray.concat(
            this.checkIfNewEntryEndTimeIsBeforeNewEntryStartTime(newEntry),
        );
        validationResultArray = validationResultArray.concat(this.checkIfNewEntryHasDuration(newEntry));

        return validationResultArray;
    }

    public checkIfNewEntryHasDuration(newEntry: TimeRecordingGeneralEntryModel): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        const validity = newEntry.endDateTime.isAfter(newEntry.startDateTime);
        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_OSR_HAS_DURATION,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_NO_DURATION'),
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkIfNewEntryEndTimeIsBeforeNewEntryStartTime(
        newEntry: TimeRecordingGeneralEntryModel,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const validity: boolean = newEntry.endDateModel.value.isSameOrAfter(newEntry.startDateModel.value);
        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_OSR_HAS_DURATION,
            <string>this.translate.instant(
                'CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_END_TIME_BEFORE_NEW_ENTRY_START_TIME',
                {
                    endTime: newEntry.endDateTime.format(this.clientManager.getDateTimeFormat(true)),
                    startTime: newEntry.startDateTime.format(this.clientManager.getDateTimeFormat(true)),
                },
            ),
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkTypeOfWork(newEntry: TimeRecordingGeneralEntryModel): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        if (!newEntry.getTypeOfWorkCode()) {
            validity = false;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_NO_TYPEOFWORK,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_NO_TYPEOFWORK'),
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkActivity(newEntry: TimeRecordingGeneralEntryModel): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        if (
            newEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.OSR &&
            !newEntry.activitiesModel.selectedEntry
        ) {
            validity = false;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_NO_ACTIVITY,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_NO_ACTIVITY'),
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkIfNewEntryStartsBeforeTRTO(
        timeRecordingEntries: TimeRecordingEntryModel[],
        newEntry: TimeRecordingGeneralEntryModel,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        const travelToEntries = this.timeRecordingService.getTravelToEntries(timeRecordingEntries);

        // If standby travel is enabled, check if new entry starts before last travel-to entry
        const entryTRTO = this.configService.getEnableStandbyTravel()
            ? travelToEntries[travelToEntries.length - 1]
            : this.timeRecordingService.getFirstTimeRecordingEntry(timeRecordingEntries);

        if (!entryTRTO) {
            throw new Error(`Could not find TRTO entry!`);
        }

        if (newEntry.startDateTime.isBefore(entryTRTO.startDateTime)) {
            validity = false;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_STARTS_BEFORE_TRTO,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_STARTS_BEFORE_TRTO'),
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkIfNewEntryStartsLaterThanTRBK(
        timeRecordingEntries: TimeRecordingEntryModel[],
        newEntry: TimeRecordingGeneralEntryModel,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        const entryTRBK = this.timeRecordingService.getLastTimeRecordingEntry(timeRecordingEntries);

        if (!entryTRBK) {
            throw new Error(`Could not find TRBK entry!`);
        }

        if (newEntry.startDateTime.isAfter(entryTRBK.startDateTime)) {
            if (!newEntry.isPhoneFix()) {
                validity = false;
            }
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_STARTS_AFTER_TRBK,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_STARTS_AFTER_TRBK'),
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkStartTime(timeRecordingEntries: TimeRecordingEntryModel[]): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const osrLimitValueHelper = this.valueHelperService.getOsrLimitValueHelper();
            const osrLimitFuture = osrLimitValueHelper ? moment.duration(osrLimitValueHelper.descText) : undefined;
            const now = moment();

            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_VALIDITY.INVALID_FUTURE_START_TIME,
                <string>this.translate.instant(
                    'CONFIRMATION_TIMERECORDING_VALIDATION_MAXIMUM_ALLOWED_STARTTIME_IN_FUTURE',
                    {
                        startTime: currentEntry.startDateTime.format(this.clientManager.getDateTimeFormat()),
                        maxAllowedFutureTime: now
                            .clone()
                            .add(osrLimitFuture)
                            .format(this.clientManager.getDateTimeFormat()),
                    },
                ),
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (currentEntry.startDateTime.isAfter(now.clone().add(osrLimitFuture))) {
                validationResultEntry.isValid = false;
            }
        });

        return validationResultArray;
    }

    public checkForSubmittedTimesheet(
        timeRecordingEntries: TimeRecordingEntryModel[],
        timesheet: TimesheetModel,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        timeRecordingEntries.forEach((timerecordingEntry) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.TIMESHEET_SUBMITTED,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_TIMESHEET_SUBMITTED', {
                    timesheetDate: timerecordingEntry.startDateModel.value.format(this.clientManager.getDateFormat()),
                }),
                true,
                timerecordingEntry.id,
            );
            if (
                timerecordingEntry.startDateModel.value.isSame(timesheet.startOfWork, 'date') &&
                timesheet.isSubmitted
            ) {
                validationResultEntry.isValid = false;
            }
            validationResultArray.push(validationResultEntry);
        });
        return validationResultArray;
    }
}
